import React from "react";
import { Card } from "./GanttCard";
import { n, hourTicks, OT_INK, OT_BAND, OT_SWATCH } from "./ganttTypes";
import type { GanttInputs, FullResult, Segment } from "./ganttTypes";
import { fmtHm, fmtDur } from "./chartUtils";

interface ScheduleCardProps {
  inputs: GanttInputs;
  result: FullResult | null;
}

// Legend wording. The bar tooltips reuse it so the two always agree.
const LEGEND = {
  load:     "Load",
  freeze:   "Freeze",
  service:  "Defrost / unload",
  flow:     "Idle — waiting on product",
  crew:     "Idle — waiting on crew",
  edge:     "Shift edge",
  crewIdle: "Crew idle",
} as const;

/** Bar class and legend wording for one segment. */
function segStyle(s: Segment): { cls: string; label: string } {
  if (s.phase === "load")    return { cls: "gseg s-load",    label: LEGEND.load };
  if (s.phase === "freeze")  return { cls: "gseg s-freeze",  label: LEGEND.freeze };
  if (s.phase === "service") return { cls: "gseg s-service", label: LEGEND.service };
  const reason = s.reason ?? "start";
  if (reason === "flow") return { cls: "gseg s-flow", label: LEGEND.flow };
  if (reason === "crew") return { cls: "gseg s-crew", label: LEGEND.crew };
  return { cls: "gseg s-start", label: LEGEND.edge };
}

/** A bar's time span for its tooltip: "0:08–2:55 (2 h 47 min)". The ends are
 *  rounded first so the duration always matches the times shown (phases can
 *  run in half minutes). */
const fmtSpan = (s: Segment) => {
  const a = Math.round(s.start_min), b = Math.round(s.end_min);
  return `${fmtHm(a)}–${fmtHm(b)} (${fmtDur(b - a)})`;
};

/**
 * Which crew did each load and defrost / unload. The engine reports how many
 * crews it ran, not which one took each task, so rebuild it from the segments:
 * in start order (ties: service first, then freezer) each task goes to the
 * lowest-numbered crew free at its start. Crews are interchangeable, so this
 * shows the same workload the simulation ran.
 */
function assignCrews(segments: Segment[], nCrews: number): Segment[][] {
  const rows = Array.from({ length: nCrews }, (): Segment[] => []);
  if (!rows.length) return rows;
  const freeAt = rows.map(() => -Infinity);
  const rank = (s: Segment) => (s.phase === "service" ? 0 : 1);
  segments
    .filter(s => s.phase === "load" || s.phase === "service")
    .sort((a, b) => a.start_min - b.start_min || rank(a) - rank(b) || a.freezer - b.freezer)
    .forEach(s => {
      let c = freeAt.findIndex(t => t <= s.start_min + 1e-6);
      // Defensive: the engine never runs more tasks at once than it has crews.
      if (c < 0) c = freeAt.reduce((best, t, k) => (t < freeAt[best] ? k : best), 0);
      rows[c].push(s);
      freeAt[c] = Math.max(freeAt[c], s.end_min);
    });
  return rows;
}

/** Translucent overtime band over a track's bars + the dashed shift-end marker. */
function OvertimeMarks({ left, testId }: { left: string; testId?: string }) {
  return (
    <>
      <div
        aria-hidden
        data-testid={testId}
        style={{
          position: "absolute", top: 0, bottom: 0, right: 0, left,
          background: OT_BAND, pointerEvents: "none",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute", top: 0, bottom: 0, width: 0, marginLeft: -1, left,
          borderLeft: `2px dashed ${OT_INK}`, pointerEvents: "none",
        }}
      />
    </>
  );
}

/**
 * Freezer schedule — one Gantt row per freezer over the shift (+ any unloading
 * overtime), then a shorter row per crew with that crew's loads and unloads.
 */
export function ScheduleCard({ inputs, result }: ScheduleCardProps) {
  const r = result?.main;
  // Time axis = the shift plus any unloading overtime the schedule used after
  // it (segments and the buffer series then run past the shift end).
  const tShiftMin = (r?.t_shift_h || n(inputs.tShift, 16)) * 60;
  const otMin = Math.max(0, r?.overtime_used_h ?? 0) * 60;
  const tEndMin = tShiftMin + otMin || 1;
  const pct = (min: number) => (min / tEndMin) * 100;
  const otLeft = `${pct(tShiftMin)}%`;
  const nF = n(inputs.nFreezers, 4);
  // Crew rows come from the result only: they show who did what in the
  // schedule on screen, so there are none until the first simulation returns.
  const nCrews = Math.max(0, Math.round(r?.n_crews || 0));

  const segsByFreezer: Record<number, Segment[]> = {};
  r?.segments.forEach(s => {
    if (!segsByFreezer[s.freezer]) segsByFreezer[s.freezer] = [];
    segsByFreezer[s.freezer].push(s);
  });
  const crewTasks = assignCrews(r?.segments ?? [], nCrews);

  const ticks = hourTicks(tShiftMin / 60, tEndMin / 60, otMin > 0);

  // Position of a bar on a freezer or crew track.
  const barPos = (s: Segment) => ({
    left:  `${pct(s.start_min)}%`,
    width: `${Math.max(0, pct(s.end_min - s.start_min))}%`,
  });

  return (
    <Card
      title="Freezer schedule"
      summary={r ? `${nF} freezers · ${r.n_crews} crews · first load ${Math.round(r.first_load_min)} min` : ""}
    >
      <div className="gtick">
        {ticks.map(t => (
          <span
            key={`${t.kind}-${t.h}`}
            title={t.kind === "shift" ? "Shift end" : t.kind === "end" ? "Last unload (end of overtime)" : undefined}
            style={{
              left: `${pct(t.h * 60)}%`,
              ...(t.kind === "shift" ? { color: OT_INK, fontWeight: 700 } : {}),
            }}
          >{t.label}</span>
        ))}
      </div>

      {Array.from({ length: nF }, (_, i) => (
        <div key={i} className="grow" data-testid={`gantt-row-f${i + 1}`}>
          <div className="glab">F{i + 1}</div>
          <div className="gtrack">
            {(segsByFreezer[i] ?? []).map((s, si) => {
              const { cls, label } = segStyle(s);
              return (
                <div
                  key={si}
                  className={cls}
                  style={barPos(s)}
                  title={`F${i + 1} · ${label} · ${fmtSpan(s)}`}
                />
              );
            })}
            {otMin > 0 && <OvertimeMarks left={otLeft} testId={`gantt-ot-band-f${i + 1}`} />}
          </div>
        </div>
      ))}

      {/* Crew rows: each crew's loads and defrost / unloads. An empty (plain)
          track is an idle crew, unlike the hatched freezer waits above. */}
      {crewTasks.length > 0 && (
        <div className="gcrew">
          {crewTasks.map((tasks, c) => (
            <div key={c} className="grow crew" data-testid={`gantt-crew-row-c${c + 1}`}>
              <div className="glab">{`Crew ${c + 1}`}</div>
              <div className="gtrack crew">
                {tasks.map((s, si) => {
                  const { cls, label } = segStyle(s);
                  return (
                    <div
                      key={si}
                      className={cls}
                      style={barPos(s)}
                      title={`Crew ${c + 1} · ${label} F${s.freezer + 1} · ${fmtSpan(s)}`}
                    />
                  );
                })}
                {otMin > 0 && <OvertimeMarks left={otLeft} testId={`gantt-ot-band-c${c + 1}`} />}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="legend">
        <div><i style={{ background: "#E0903A" }} />{LEGEND.load}</div>
        <div><i style={{ background: "#CFE0EF", border: "1px solid #c2d6ec" }} />{LEGEND.freeze}</div>
        <div><i style={{ background: "#2FA36A" }} />{LEGEND.service}</div>
        <div><i style={{ background: "repeating-linear-gradient(45deg,#f6d29a 0 4px,#fbe7c4 4px 8px)" }} />{LEGEND.flow}</div>
        <div><i style={{ background: "repeating-linear-gradient(45deg,#f0b0b0 0 4px,#f9dada 4px 8px)" }} />{LEGEND.crew}</div>
        <div><i style={{ background: "repeating-linear-gradient(45deg,#dfe7ef 0 3px,#eef3f8 3px 6px)" }} />{LEGEND.edge}</div>
        {crewTasks.length > 0 && (
          <div data-testid="gantt-legend-crew-idle">
            <i className="crew-idle" />{LEGEND.crewIdle}
          </div>
        )}
        {otMin > 0 && (
          <div data-testid="gantt-legend-overtime">
            <i style={{ background: OT_SWATCH, border: `1px solid ${OT_INK}` }} />Overtime (unload only)
          </div>
        )}
      </div>
    </Card>
  );
}
